import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Line } from 'react-chartjs-2';
import styled from 'styled-components';
import { Avatar, Icon } from '~/components';
import { useApi } from '~/contexts';
import { colors, weights } from '~/styles';
import { NoData, formatCost, formatNumber } from './shared';

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: 0.75rem 1rem;
  text-align: ${({ align }) => align || 'left'};
  font-weight: ${weights.bold};
  font-size: 0.875rem;
  color: ${colors.grey50};
  border-bottom: solid 2px ${colors.grey10};
`;

const Td = styled.td`
  padding: 0.75rem 1rem;
  text-align: ${({ align }) => align || 'left'};
  font-size: 0.875rem;
  border-bottom: solid 1px ${colors.grey10};
  vertical-align: middle;
`;

const WorkspaceCell = styled.div`
  display: flex;
  align-items: center;
`;

const WorkspaceLogo = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
`;

const WorkspaceImage = styled.img`
  max-height: 100%;
  object-fit: contain;
`;

const WorkspaceInfo = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 0.5rem;
  min-width: 0;
`;

const WorkspaceName = styled.div`
  font-weight: ${weights.bold};
  font-size: 0.8125rem;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const WorkspaceKey = styled.div`
  color: ${colors.grey55};
  font-size: 0.6875rem;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const MemberCell = styled.div`
  display: flex;
  align-items: center;
`;

const MemberInfo = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 0.75rem;
`;

const MemberName = styled.div`
  font-weight: ${weights.black};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const MemberEmail = styled.div`
  color: ${colors.grey55};
  font-size: 0.75rem;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const SparklineCell = styled.div`
  height: 2.5rem;
`;

const sparklineOptions = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: { display: false },
    y: { display: false },
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      enabled: true,
      mode: 'index',
      intersect: false,
      callbacks: {
        title: ([tooltip]) => moment.utc(tooltip.label).format('MMM D'),
        label: (context) => `${context.raw.toLocaleString()} tokens`,
      },
    },
  },
  elements: {
    point: { radius: 0, hoverRadius: 3 },
  },
};

function Sparkline({ overTime }) {
  const hasData = useMemo(() => (overTime || []).some((d) => d.tokens > 0), [overTime]);

  const chartData = useMemo(() => {
    if (!hasData || !overTime || overTime.length < 2) return null;
    return {
      labels: overTime.map((d) => d.date),
      datasets: [
        {
          data: overTime.map((d) => d.tokens),
          backgroundColor: colors.primary + '40',
          borderColor: colors.primary,
          borderWidth: 1.5,
          fill: true,
          tension: 0.4,
        },
      ],
    };
  }, [overTime, hasData]);

  if (!chartData) return null;

  return (
    <SparklineCell>
      <Line data={chartData} options={sparklineOptions} />
    </SparklineCell>
  );
}

export default function Members({ start, end }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!start || !end) return;
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      setData(null);
      try {
        const { data } = await api.admin.aiDashboard.getMembers({ start, end });
        if (!cancelled) setData(data);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [api, start, end]);

  if (isLoading || !data) return null;

  if (data.length === 0) {
    return <NoData>No data available for the selected period.</NoData>;
  }

  return (
    <div>
      <Table>
        <thead>
          <tr>
            <Th>Member</Th>
            <Th>Workspace</Th>
            <Th align="right">Conversations</Th>
            <Th align="right">Requests</Th>
            <Th align="right">Cost</Th>
            <Th>Usage</Th>
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr key={row.memberId}>
              <Td>
                <MemberCell>
                  <Avatar
                    value={{ name: row.memberName, imageUrl: row.memberImageUrl }}
                    size={40}
                    isCircle
                    hasBackground
                  />
                  <MemberInfo>
                    <MemberName>{row.memberName}</MemberName>
                    <MemberEmail>{row.memberEmail}</MemberEmail>
                  </MemberInfo>
                </MemberCell>
              </Td>
              <Td>
                <WorkspaceCell>
                  <WorkspaceLogo>
                    {row.workspaceImageUrl ? (
                      <WorkspaceImage src={row.workspaceImageUrl} alt={row.workspaceName} />
                    ) : (
                      <Icon icon="building" style={{ fontSize: '1.25rem', color: colors.grey10 }} />
                    )}
                  </WorkspaceLogo>
                  <WorkspaceInfo>
                    <WorkspaceName>{row.workspaceName}</WorkspaceName>
                    <WorkspaceKey>{row.workspaceKey}</WorkspaceKey>
                  </WorkspaceInfo>
                </WorkspaceCell>
              </Td>
              <Td align="right">{formatNumber(row.conversations)}</Td>
              <Td align="right">{formatNumber(row.requests)}</Td>
              <Td align="right">{formatCost(row.totalCost)}</Td>
              <Td>
                <Sparkline overTime={row.overTime} />
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}
